import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Divider,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const invitationToken = searchParams.get('invitationToken');

  const { login, getUserDashboardRoute } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const stateMsg = (location.state as any)?.message;
    if (stateMsg) {
      setSuccess(String(stateMsg));
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      
      // If there's an invitation token, accept it after login
      if (invitationToken) {
        try {
          await apiService.post('/existing-customers/onboard/accept', { token: invitationToken });
          setSuccess('Welcome back! Invitation accepted successfully.');
        } catch (inviteError: any) {
          console.error('Failed to accept invitation:', inviteError);
          setError(inviteError.message || 'Login successful but failed to accept invitation. You may already have accepted it.');
        }
      }
      
      const redirect = searchParams.get('redirect');
      const safeRedirect = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null;
      const dashboardRoute = invitationToken
        ? '/customer?onboarding=1'
        : safeRedirect || getUserDashboardRoute(user);
      navigate(dashboardRoute, { replace: true });
    } catch (err: any) {
      console.error('[LOGIN-PAGE] Login error:', {
        message: err.message,
        status: err.status,
        details: err.details,
        raw: err.raw
      });
      // Show more specific error messages
      let errorMessage = 'Login failed';
      if (err.status === 409 || err.raw?.code === 'DEMO_IN_USE') {
        errorMessage =
          err.message ||
          'LawnHub Interactive Demo seats are currently filled — we’re getting a lot of interest right now! Please try again in a few minutes.';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (err.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message?.includes('Network error')) {
        errorMessage = 'Network error. Please check your connection.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography component="h1" variant="h4" gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign in to your LawnHub account
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {invitationToken && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have a pending invitation. Log in to accept it.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot your password?
            </Link>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link component={RouterLink} to="/register" variant="body2">
                Create a free account
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
